/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import curry from 'lodash/curryRight';
import ModalBox from '../../shared/ModalBox/ModalBox';
import createUser from './AddUserImage.png';
import createSpaceImg from '../CreateSpace/createSpaceImage.png';
import RightPanel from './RightPanel';
import { getUniqData } from '../../../utils/duplicate';
import AddUserConfirmation from './AddUserConfirmation';
import AddUserSuccess from './AddUserSuccess';
import chunkProcess from '../../../utils/chunkProcess';
import { analyticsTrackEvent } from '../../../utils/analytics';
import { ADMIN_ROLE_ASSIGNED_TO_USER } from '../../../constants/analytics';

const LEGACY_ROLES = [
  { id: 'student', title: 'Student' },
  { id: 'educator', title: 'Educator' },
];

const confirmationSectionConfig = (roles) => ({
  getConfirmationMessage: (data) => (
    <AddUserConfirmation {...data} roles={roles} />
  ),
  getSuccessMessage: (data) => <AddUserSuccess {...data} roles={roles} />,
});

const MODAL_PROPS = (
  props: any = {
    usersSearchData: [],
    spacesSearchData: [],
    search: null,
  },
  onSelect: (..._args) => void = () => {},
) => {
  const curriedSearch = props.search ? curry(props.search) : (_) => {};
  return {
    users: {
      title: 'user',
      modalSubtitle: 'Add users to spaces',
      rightPanelConfig: {
        getRightPanelTitle: (entityName) => `Creating ${entityName}`,
      },
      create: props.createUsers,
      uniqueness: {
        columns: ['externalId'],
      },
      csvLeftPanelAction: {
        tabTitle: 'import a CSV of users',
        actionButton: {
          title: 'Add users',
        },
      },
      columns: ['externalId'],
      table: {
        image: createUser,
        onSelect: (selected) => onSelect(selected, 'user'),
      },
      search: {
        data: props.usersSearchData,
        extractSelected: (item) => ({
          name: item.title,
          externalId: item.subtitle,
          id: item.id,
        }),
        search: curriedSearch(['users']),
        isSearching: props.isSearching,
        clearSearchOnSelect: true,
      },
    },
    spaces: {
      title: 'space',
      modalSubtitle: 'Add users to spaces',
      rightPanelConfig: {
        getRightPanelTitle: (entityName) => `Creating ${entityName}`,
      },
      csvLeftPanelAction: {
        tabTitle: 'import a CSV of spaces',
        actionButton: {
          title: 'Add spaces',
        },
      },
      columns: ['externalId'],
      table: {
        image: createSpaceImg,
        onSelect: (selected) => onSelect(selected, 'space'),
      },
      uniqueness: {
        columns: ['externalId'],
      },
      search: {
        search: curriedSearch(['spaces']),
        isSearching: props.isSearching,
        data: props.spacesSearchData,
        extractSelected: (item) => ({
          name: item.title,
          externalId: item.subtitle,
          id: item.id,
        }),
        clearSearchOnSelect: true,
      },
    },
  };
};

const CHUNK_SIZE = 100;

interface AddUserModalProps extends RouteComponentProps {
  search?: (term: unknown, searchFor?: unknown) => void;
  createRoles?: (roles: unknown[]) => void;
  fetchRoles?: () => void;
  usersSearchData?: unknown[];
  spacesSearchData?: unknown[];
  isSearching?: boolean;
  addingUsers?: unknown[];
  sso?: boolean;
  roles?: Array<{ id: string; title?: string; name?: string; _id?: string }>;
  flags?: { [key: string]: any };
}

class AddUserModal extends Component<AddUserModalProps> {
  static defaultProps: Partial<AddUserModalProps>;

  state = {
    step: 'users',
    modalProps: MODAL_PROPS().users,
    loading: false,
    confirmModalProps: null,
    leftToProcess: null,
    roleId: null,
    selected: {
      user: [],
      space: [],
    },
    errors: undefined,
    totalToProcess: undefined,
  };

  static getDerivedStateFromProps = (nextProps, prevState) => ({
    ...prevState,
    modalProps: MODAL_PROPS(nextProps)[prevState.step],
  });

  componentWillMount = () => {
    this.setState({
      modalProps: MODAL_PROPS(this.props)[this.state.step],
    });
  };

  componentDidMount = () => {
    const { webAppFtForRbac202505 } = this.props.flags || {};
    // Only fetch roles if they're not already loaded
    if (
      webAppFtForRbac202505 &&
      this.props.fetchRoles &&
      (!this.props.roles || this.props.roles.length === 0)
    ) {
      this.props.fetchRoles();
    }
    // Set default roleId if roles are already available
    if (
      webAppFtForRbac202505 &&
      this.props.roles &&
      this.props.roles.length > 0 &&
      !this.state.roleId
    ) {
      const firstRole = this.props.roles[0];
      const roleId = firstRole.id || firstRole._id;
      if (roleId) {
        this.setState({ roleId });
      }
    } else if (!webAppFtForRbac202505 && !this.state.roleId) {
      this.setState({ roleId: 'student' });
    }
  };

  componentDidUpdate = (prevProps) => {
    const { webAppFtForRbac202505 } = this.props.flags || {};
    // Set default roleId when roles are loaded (only if roles changed from empty to having data)
    if (
      webAppFtForRbac202505 &&
      this.props.roles &&
      this.props.roles.length > 0 &&
      !this.state.roleId &&
      (!prevProps.roles || prevProps.roles.length === 0)
    ) {
      const firstRole = this.props.roles[0];
      const roleId = firstRole.id || firstRole._id;
      if (roleId) {
        this.setState({ roleId });
      }
    }
  };

  getRoleString = (roleId) => {
    if (roleId === 'student') {
      return 'student';
    }
    if (roleId === 'educator') {
      return 'educator';
    }
    if (!roleId || !this.props.roles) {
      return 'educator';
    }
    const role = this.props.roles.find((r) => (r.id || r._id) === roleId);
    if (role) {
      const roleName = (role.title || role.name || '').toLowerCase();
      // If the selected role is "student", return "student", otherwise return "educator"
      return roleName === 'student' ? 'student' : 'educator';
    }
    return 'educator';
  };

  getRoleName = (roleId) => {
    if (!roleId || !this.props.roles) {
      return null;
    }
    const role = this.props.roles.find((r) => (r.id || r._id) === roleId);
    return role ? role.title || role.name || null : null;
  };

  transformAddUsersData = (data) => {
    const { webAppFtForRbac202505 } = this.props.flags || {};
    const users = getUniqData(data.user);
    const spaces = getUniqData(data.space);
    const roleString = this.getRoleString(this.state.roleId);
    return users.reduce(
      (acc, user) => [
        ...acc,
        ...spaces.map((space) => ({
          externalUserId: user.externalId,
          externalSpaceId: space.externalId,
          spaceId: space?.id,
          role: roleString,
          roleId: webAppFtForRbac202505 ? this.state.roleId : undefined,
          userId: user?.id || undefined,
        })),
      ],
      [],
    );
  };

  onSave = async (data) => {
    const users = this.transformAddUsersData(data);

    if (!users.length) {
      return true;
    }

    // Strip userId/spaceId before calling the roles API – backend does not expect them.
    const rolesPayload = users.map(
      ({ userId: _userId, spaceId: _spaceId, ...rest }) => rest,
    );

    await chunkProcess({
      data: rolesPayload,
      chunkSize: CHUNK_SIZE,
      onRequest: this.props.createRoles,
      onProgress: (newData) => this.setState(newData),
      onError: (error) =>
        this.setState({
          errors: [...(this.state.errors || []), error],
        }),
    });

    const { roleId } = this.state;
    const roleName = this.getRoleName(roleId);
    if (roleId) {
      const failedPairs = new Set<string>();

      (this.state.errors || []).forEach((error: any) => {
        const { metadata } = error || {};
        if (Array.isArray(metadata)) {
          metadata.forEach((m) => {
            const externalId = m && m.externalId;
            if (
              externalId &&
              externalId.externalUserId &&
              externalId.externalSpaceId
            ) {
              failedPairs.add(
                `${externalId.externalUserId}::${externalId.externalSpaceId}`,
              );
            }
          });
        }
      });

      users.forEach(({ externalUserId, externalSpaceId, userId, spaceId }) => {
        const key = `${externalUserId}::${externalSpaceId}`;
        if (failedPairs.has(key)) {
          return;
        }

        analyticsTrackEvent(ADMIN_ROLE_ASSIGNED_TO_USER, {
          space_id: spaceId,
          role_id: roleId,
          role_name: roleName,
          source: 'admin_space',
          assigned_user_id: userId,
        });
      });
    }

    return true;
  };

  nextStep = () => {
    if (this.state.step === 'users') {
      this.setState({
        step: 'spaces',
        modalProps: MODAL_PROPS(this.props, () => undefined).spaces,
      });
    }
  };

  prevStep = () => {
    this.setState({
      step: 'users',
      modalProps: MODAL_PROPS(this.props, () => undefined).users,
    });
  };

  onRoleChange = (roleId) => {
    this.setState({
      roleId,
    });
  };

  render() {
    const { addingUsers, roles } = this.props;
    const {
      step,
      roleId,
      errors,
      selected,
      modalProps,
      leftToProcess,
      totalToProcess,
      confirmModalProps,
    } = this.state;
    const { webAppFtForRbac202505 } = this.props.flags || {};
    const effectiveRoles = webAppFtForRbac202505 ? roles || [] : LEGACY_ROLES;

    return (
      <div>
        <ModalBox
          {...modalProps}
          selectedRole={roleId}
          renderRightPanel={(props) => (
            <RightPanel
              {...props}
              onRoleChange={this.onRoleChange}
              selectedRole={roleId}
              roles={effectiveRoles}
              step={step}
            />
          )}
          selected={selected}
          addingUsers={addingUsers}
          confirmModalProps={confirmModalProps}
          onNext={this.nextStep}
          onSave={this.onSave}
          onPrev={this.prevStep}
          leftToProcess={leftToProcess}
          totalToProcess={totalToProcess}
          errorsList={errors}
          step={step}
          showInstitutionDetails
          confirmationSectionConfig={confirmationSectionConfig(effectiveRoles)}
        />
      </div>
    );
  }
}

AddUserModal.defaultProps = {
  search: () => {},
  createRoles: () => {},
  fetchRoles: () => {},
  roles: [],
};

export default withStyles(
  {},
  { withTheme: true },
)(withRouter(withLDConsumer()(AddUserModal)));
